"use client"

/**
 * PillarboxAd — renders an ad in the black gutter beside portrait content on
 * a wide (desktop) viewport. This is LetterboxAd's counterpart: LetterboxAd
 * fires when the media is "wider" than its frame (bars top/bottom);
 * PillarboxAd fires in the opposite case, when the media is "taller" than
 * its frame relative to the container (bars left/right) — a portrait image
 * or video shown on a desktop-width modal, the classic side-gutter case.
 *
 * Both gutters can now carry an ad — same fix as LetterboxAd's top bar
 * (see letterboxTopAdData's comment in booru-detail-modal.tsx): the
 * dupe-inventory problem was never "two gutters," it was "the same zone ID
 * in both." Caller passes a genuinely distinct zone (a different tier's
 * real ad_zones row) per `side`, same as the two letterbox bars.
 *
 * Sized for a 160x600 Vertical Skyscraper creative (the correct shape for a
 * tall narrow gutter — a horizontal format like a 728x90 leaderboard would
 * render sideways-wrong here). Scaled to fit rather than stretched or
 * clipped, same as LetterboxAd — we can't measure inside the sandboxed
 * iframe, but the zone's nominal creative size is known from the outside.
 */

import { useState, useEffect } from "react"
import { X } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { useHasConsent } from "@/components/cookie-consent"
import { isAdCooldownExpired, markAdShown } from "@/lib/ad-cooldown"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { ZoneImpressionBeacon } from "@/components/ads/ZoneImpressionBeacon"
import { trackZoneClick } from "@/lib/ad-zone-tracking"
import { useTranslations } from "next-intl"

interface PillarboxAdProps {
  /** Aspect ratio of the media content (width / height) */
  mediaAspectRatio: number
  /** Aspect ratio of the container (containerWidth / containerHeight) */
  containerAspectRatio: number
  /** Pixel height of the media container — required for the px-based caps */
  containerHeightPx?: number | null
  /** Nominal size of the ad zone's creative, used to scale it to fit */
  creativeWidth?: number
  creativeHeight?: number
  /** HTML embed / JS tag from ad network — rendered in a sandboxed iframe */
  embedHtml?: string | null
  /** Direct image banner URL */
  bannerImageUrl?: string | null
  /** Click-through URL for banner */
  bannerClickUrl?: string | null
  /** Label shown in corner */
  label?: string
  onDismiss?: () => void
  className?: string
  /** Which gutter this instance renders in. Defaults to "right" — the
   * original, single-sided behavior — so any other caller is unaffected. */
  side?: "left" | "right"
  /**
   * Our ad_zones UUID, used to report impression/click. Without it this
   * placement records nothing — pillarbox ("slide-out") ads were rendering to
   * real users completely untracked. NOT the numeric ExoClick id.
   */
  zoneId?: string | null
}

/** Below this, the gutter is too narrow for even a heavily-scaled skyscraper. */
const MIN_AD_WIDTH_PX = 60
/** Don't stretch past the native creative width just because the gutter is wide. */
const MAX_AD_WIDTH_PX = 200
/** Leave a little breathing room top/bottom rather than touching the modal edges. */
const VERTICAL_MARGIN_PX = 24

export function PillarboxAd({
  mediaAspectRatio,
  containerAspectRatio,
  containerHeightPx,
  creativeWidth = 160,
  creativeHeight = 600,
  embedHtml,
  bannerImageUrl,
  bannerClickUrl,
  label = "Ad",
  onDismiss,
  className,
  zoneId,
  side = "right",
}: PillarboxAdProps) {
  const t = useTranslations("pillarboxAd")
  const hasConsent = useHasConsent()
  // Frequency gate. This placement previously had NONE — it rendered on every
  // media open whose geometry left a bar, which is what made it feel like
  // spam. Computed in a useState initializer (not a bare call) so the verdict
  // is stable for the component's life and can't flip mid-render, and so the
  // hook stays above every early return below.
  const [freqAllows] = useState(() => {
    const store = useAdFrequencyStore.getState()
    if (!store.isEnabled("pillarbox_ads")) return false
    return isAdCooldownExpired("pillarbox", store.getCooldownMinutes("pillarbox_ads"))
  })

  useEffect(() => {
    if (freqAllows) markAdShown("pillarbox")
  }, [freqAllows])

  if (!hasConsent) return null
  if (!freqAllows) return null
  if (!containerHeightPx || containerHeightPx <= 0 || containerAspectRatio <= 0) return null

  // Side gutters only exist when the media is "narrower" relative to its
  // height than the container is — i.e. mediaAR < containerAR (a portrait
  // image/video on a wide desktop viewport). When mediaAR >= containerAR the
  // letterboxing is top/bottom, not sides — that's LetterboxAd's case, not
  // this one.
  if (mediaAspectRatio <= 0 || mediaAspectRatio >= containerAspectRatio) return null

  const containerWidthPx = containerAspectRatio * containerHeightPx
  const mediaRenderedWidthPx = containerHeightPx * mediaAspectRatio
  const gutterPx = (containerWidthPx - mediaRenderedWidthPx) / 2

  const adWidth = Math.min(gutterPx, MAX_AD_WIDTH_PX)
  if (adWidth < MIN_AD_WIDTH_PX) return null

  const adHeight = Math.min(containerHeightPx - VERTICAL_MARGIN_PX * 2, creativeHeight)
  // Never upscale a creative past 1:1 — only shrink to fit.
  const scale = Math.min(1, adWidth / creativeWidth, adHeight / creativeHeight)

  return (
    <div
      className={cn(
        "absolute top-0 bottom-0 flex items-center justify-center pointer-events-auto z-[5]",
        side === "left" ? "left-0" : "right-0",
        className,
      )}
      style={{ width: gutterPx }}
    >
      <div
        className="relative flex items-center justify-center overflow-hidden bg-black/40 rounded"
        style={{ width: adWidth, height: adHeight }}
      >
        <ZoneImpressionBeacon zoneId={zoneId} />
        {/* Ad label */}
        <span className="absolute top-1 left-1.5 text-[9px] text-white/40 select-none z-10 pointer-events-none">
          {label}
        </span>

        {/* Dismiss */}
        {onDismiss && (
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss() }}
            className="absolute top-0.5 right-0.5 z-10 text-white/40 hover:text-white/80 transition-colors p-0.5"
            aria-label={t("closeAd")}
          >
            <X className="h-3 w-3" />
          </button>
        )}

        {/* Banner image */}
        {bannerImageUrl && (
          <a
            href={bannerClickUrl ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full h-full"
            onClick={(e) => {
              e.stopPropagation()
              // Banner path only — clicks inside the sandboxed cross-origin
              // js_embed iframe below are not observable to us.
              trackZoneClick({ zoneId })
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={bannerImageUrl} alt={t("advertisement")} className="w-full h-full object-contain" />
          </a>
        )}

        {/* HTML/JS embed — sandboxed iframe (allow-scripts only, no
            allow-same-origin/allow-popups/allow-top-navigation). Same
            treatment as every other network embed on this site — a
            same-origin script here could reach outside its own box and
            interfere with the rest of the page. */}
        {!bannerImageUrl && embedHtml && (() => {
          // ExoClick's NUMERIC id, renamed so it no longer shadows the
          // ad_zones UUID prop — sending this to zone-interact is rejected.
          const exoZoneId = extractExoclickZoneId(embedHtml)
          const style = {
            width: creativeWidth,
            height: creativeHeight,
            border: 0,
            display: "block" as const,
            transform: scale < 1 ? `scale(${scale})` : undefined,
            transformOrigin: "center",
            flexShrink: 0,
          }
          // See lib/ad-embed.ts — srcDoc+allow-scripts-only gives the frame
          // an opaque origin, which blocks ExoClick's cookie write and the
          // creative never renders.
          return exoZoneId ? (
            <iframe src={exoclickIframeSrc(exoZoneId)} sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" title={t("advertisement")} scrolling="no" style={style} />
          ) : (
            <iframe
              srcDoc={`<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${embedHtml}</body></html>`}
              sandbox="allow-scripts"
              title={t("advertisement")}
              scrolling="no"
              style={style}
            />
          )
        })()}
      </div>
    </div>
  )
}
